<script setup lang="ts">
  // Ported from cartadigital-pos app/components/pos/order/PosOrderViewInfo.vue,
  // fed by admin's IOrder (injected 'order' ref, same contract as the old
  // ModalPosViewOrder children).
  import { computed, getCurrentInstance, inject, Ref } from 'vue';
  import { useNow } from '@vueuse/core';
  import { OrderPaymentStatus, OrderStatus, OrderType } from '@/enums/OrderEnums';
  import { IOrder } from '@/interfaces/IOrder';
  import { getOrderHumanTitle } from '@/domain/pos/order-formatting';
  import { formatRelativeAge } from '@/domain/pos/relative-age';
  import {
    getPosOrderTypeIcon,
    getPosOrderTypeLabel,
  } from '@/domain/pos/order-type-ui';
  import { getClubMemberPanelState } from '@/domain/club/club-member-panel';
  import { useClipboard } from '@/composition/use-clipboard';
  import { ORDER_CLUB_CONTEXT_KEY } from '@/composition/use-order-club-context';
  import { usePosMoney } from '@/composition/use-pos-money';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';

  const order = inject<Ref<IOrder>>('order')!;
  // Null under legacy hosts: only the embed's points pill renders then.
  const clubContext = inject(ORDER_CLUB_CONTEXT_KEY, null);

  const restaurantStore = useRestaurantStore();
  const clipboard = useClipboard();
  const money = usePosMoney();

  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const now = useNow({ interval: 30_000 });

  const humanTitle = computed(() => {
    const o = order.value;
    return getOrderHumanTitle({
      type: o.type,
      humanId: o.humanId ?? null,
      tableName: o.table?.name ?? null,
      customerName: o.userName ?? null,
      customerPhone: o.userTel || o.userWhatsapp || null,
      deliveryAddress: o.userAddress ?? null,
      deliveryAddress2: o.userAddress2 ?? null,
    });
  });

  const orderTypeIcon = computed(() => getPosOrderTypeIcon(order.value.type));

  const orderTypeLabel = computed(() => {
    const o = order.value;
    if (o.type === OrderType.Seat && o.table?.name) {
      return i18nT('posV2.tableWithName', { name: o.table.name });
    }
    return getPosOrderTypeLabel(o.type);
  });

  const relativeTime = computed(() =>
    formatRelativeAge(order.value.createdAt, now.value, i18nT)
  );

  const absoluteTime = computed(() => {
    const date = new Date(order.value.createdAt);
    if (!Number.isFinite(date.getTime())) return '';
    return date.toLocaleString(proxy.$i18n.locale, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  });

  const isPaid = computed(
    () => order.value.paymentStatus === OrderPaymentStatus.Paid
  );

  // Client-derived: the server never writes OrderPaymentStatus.Partial —
  // a partially-paid order (checkout club-balance debit) is totalPaid > 0
  // while still Awaiting.
  const isPartiallyPaid = computed(
    () => !isPaid.value && Number(order.value.totalPaid ?? 0) > 0
  );

  const needsAttention = computed(
    () => order.value.status === OrderStatus.Received
  );

  const orderTel = computed(
    () => order.value.userTel || order.value.userWhatsapp || null
  );

  const telLinks = computed(() => {
    const tel = orderTel.value;
    if (!tel) return null;

    const wappMessage = i18nT('posV2.whatsappGreeting', {
      restaurant: restaurantStore.restaurant?.name ?? '',
    });

    return {
      whatsapp: `https://wa.me/${tel
        .replace('+', '')
        .replace('-', '')}?text=${encodeURIComponent(wappMessage)}`,
      phone: `tel:${tel}`,
    };
  });

  const orderAddress = computed(() => {
    const o = order.value;
    if (o.type !== OrderType.Delivery || !o.userAddress) return null;
    return o.userAddress2 ? `${o.userAddress}, ${o.userAddress2}` : o.userAddress;
  });

  const mapLink = computed(() => {
    const o = order.value;
    if (o.type !== OrderType.Delivery || !o.deliveryLat) return null;
    return `https://www.google.com/maps/search/?api=1&query=${o.deliveryLat},${o.deliveryLng}`;
  });

  const coversText = computed(() => {
    const o = order.value;
    if (o.type !== OrderType.Seat) return null;

    // Session embed is authoritative when present (covers null = cleared,
    // never fall back to the stale legacy per-order value).
    const session = o.tableSession;
    if (session) {
      if (session.covers != null) return String(session.covers);
      if (session.coversSuggested != null) {
        return `${session.coversSuggested} ${i18nT('posV2.suggestedSuffix')}`;
      }
      return null;
    }

    if (o.covers != null) return String(o.covers);
    if (o.coversSuggested != null) {
      return `${o.coversSuggested} ${i18nT('posV2.suggestedSuffix')}`;
    }
    return null;
  });

  const filledCustomFields = computed(() =>
    (order.value.orderFields ?? []).filter((f) => !!f.value)
  );

  const hasExtras = computed(
    () =>
      filledCustomFields.value.length > 0 || !!order.value.userAddressComments
  );

  const originSourceText = computed(() => {
    const o = order.value;
    if (!o.manual) return i18nT('posV2.sourceDigitalMenu');
    if (o.deviceId === restaurantStore.deviceId) {
      return i18nT('posV2.sourceThisPos');
    }
    return i18nT('posV2.sourceOtherPos');
  });

  //#region Club pills

  const clubMemberFigures = computed(() => {
    const embed = order.value.clubMember;
    if (!embed) return null;
    const live = clubContext?.member.value ?? null;
    return {
      pointsBalance: live?.pointsBalance ?? embed.pointsBalance,
      // Unknown until the context loads (the embed carries no wallet).
      cashTotal: live?.cashTotal ?? null,
    };
  });

  const clubPanelState = computed(() => {
    const figures = clubMemberFigures.value;
    if (!figures) return null;
    return getClubMemberPanelState({
      program: restaurantStore.restaurant?.club,
      branchId: restaurantStore.selectedBranch?.id,
      member: figures,
      order: order.value,
    });
  });

  const clubPointsPill = computed(() => {
    const figures = clubMemberFigures.value;
    const state = clubPanelState.value;
    if (!figures || !state) return null;
    return {
      usable: state.points.usable,
      // No money value (program off / no rate): plain points only.
      label:
        state.points.value > 0
          ? i18nT('posV2.clubPillPoints', {
              points: figures.pointsBalance,
              value: money.formatComputedMoney(state.points.value),
            })
          : `${figures.pointsBalance} ${i18nT('posV2.clubPointsSuffix')}`,
    };
  });

  // Rendered once the wallet figure is known; hidden when the program has
  // no balance feature or the context could not load.
  const clubBalancePill = computed(() => {
    const figures = clubMemberFigures.value;
    const state = clubPanelState.value;
    if (!figures || !state || figures.cashTotal == null) return null;
    if (state.balance.reason === 'disabled') return null;
    const hasBalance = figures.cashTotal > 0;
    return {
      hasBalance,
      label: hasBalance
        ? i18nT('posV2.clubPillBalance', {
            value: money.formatComputedMoney(figures.cashTotal),
          })
        : i18nT('posV2.clubNoBalance'),
    };
  });

  //#endregion

  function handleCopyId() {
    clipboard.copyText(String(order.value.id));
  }

  function handleCopyAddress() {
    if (!orderAddress.value) return;
    clipboard.copyText(orderAddress.value);
  }
</script>

<template>
  <div class="pos-v2-view-info">
    <!-- Header -->
    <div class="info-header">
      <div class="header-row">
        <div class="title-group">
          <PosUiIcon name="receipt" class="title-icon" />
          <h1 class="title">{{ humanTitle }}</h1>
        </div>

        <button type="button" class="order-id" @click="handleCopyId">
          #{{ order.id }}
          <PosUiIcon name="content-copy" class="copy-icon" />
        </button>
      </div>

      <div class="chips-row">
        <span :title="absoluteTime">
          <PosUiBadge color="neutral" variant="soft" size="sm" icon="clock-outline">
            {{ relativeTime }}
          </PosUiBadge>
        </span>

        <PosUiBadge
          color="neutral"
          variant="soft"
          size="sm"
          :icon="orderTypeIcon"
        >
          {{ orderTypeLabel }}
        </PosUiBadge>

        <PosUiBadge
          v-if="isPaid"
          color="success"
          variant="soft"
          size="sm"
          icon="check-circle"
        >
          {{ $t('posV2.paidBadge') }}
        </PosUiBadge>

        <PosUiBadge
          v-if="isPartiallyPaid"
          color="warning"
          variant="soft"
          size="sm"
          icon="circle-half-full"
        >
          {{ $t('posV2.payPartial') }}
        </PosUiBadge>

        <span v-if="needsAttention" :title="$t('posV2.attentionTooltip')">
          <PosUiBadge color="error" variant="solid" size="sm" icon="alert">
            {{ $t('posV2.attentionBadge') }}
          </PosUiBadge>
        </span>
      </div>
    </div>

    <!-- Meta grid + comment -->
    <div class="meta-section">
      <div class="meta-grid">
        <div v-if="order.type === OrderType.Seat && order.table" class="meta-item">
          <span class="meta-label">
            <PosUiIcon name="silverware-fork-knife" class="meta-icon" />
            {{ $t('posV2.metaTable') }}
          </span>
          <span class="meta-value">{{ order.table.name }}</span>
        </div>

        <div v-if="coversText" class="meta-item">
          <span class="meta-label">
            <PosUiIcon name="account-group" class="meta-icon" />
            {{ $t('posV2.covers') }}
          </span>
          <span class="meta-value">{{ coversText }}</span>
        </div>

        <div v-if="order.userName" class="meta-item">
          <span class="meta-label">
            <PosUiIcon name="account" class="meta-icon" />
            {{ $t('posV2.metaCustomer') }}
          </span>
          <span class="meta-value">{{ order.userName }}</span>
        </div>

        <div v-if="order.clubMember" class="meta-item meta-item--wide">
          <span class="meta-label">
            <PosUiIcon name="star-circle" class="meta-icon" />
            {{ $t('posV2.metaClub') }}
          </span>
          <span class="meta-value">
            {{ order.clubMember.name }}
            <span class="club-member-number">
              {{
                $t('posV2.clubMemberNumber', {
                  number: order.clubMember.memberNumber,
                })
              }}
            </span>
          </span>
          <span class="club-pills">
            <PosUiBadge
              v-if="clubPointsPill"
              :color="clubPointsPill.usable ? 'primary' : 'neutral'"
              variant="soft"
              size="sm"
              icon="star-circle"
              class="club-pill"
            >
              {{ clubPointsPill.label }}
            </PosUiBadge>
            <PosUiBadge
              v-if="clubBalancePill"
              :color="clubBalancePill.hasBalance ? 'success' : 'neutral'"
              variant="soft"
              size="sm"
              icon="wallet"
              class="club-pill"
            >
              {{ clubBalancePill.label }}
            </PosUiBadge>
          </span>
        </div>

        <div v-if="restaurantStore.selectedBranch?.name" class="meta-item">
          <span class="meta-label">
            <PosUiIcon name="store" class="meta-icon" />
            {{ $t('posV2.metaBranch') }}
          </span>
          <span class="meta-value">{{ restaurantStore.selectedBranch.name }}</span>
        </div>

        <div v-if="telLinks" class="meta-item">
          <span class="meta-label">
            <PosUiIcon name="phone" class="meta-icon" />
            {{ $t('posV2.metaPhone') }}
          </span>
          <span class="meta-value meta-phone">
            <a :href="telLinks.phone" class="phone-link">{{ orderTel }}</a>
            <a
              :href="telLinks.whatsapp"
              target="_blank"
              rel="noopener"
              class="phone-action"
              aria-label="WhatsApp"
            >
              <PosUiIcon name="whatsapp" />
            </a>
          </span>
        </div>

        <div v-if="orderAddress" class="meta-item meta-item--wide">
          <span class="meta-label">
            <PosUiIcon name="map-marker" class="meta-icon" />
            {{ $t('posV2.metaAddress') }}
          </span>
          <span class="meta-value meta-address">
            <span class="address-text">{{ orderAddress }}</span>
            <a
              v-if="mapLink"
              :href="mapLink"
              target="_blank"
              rel="noopener"
              class="address-action"
              aria-label="Google Maps"
            >
              <PosUiIcon name="map-search" />
            </a>
            <button
              type="button"
              class="address-action"
              :aria-label="$t('posV2.metaAddress')"
              @click="handleCopyAddress"
            >
              <PosUiIcon name="content-copy" />
            </button>
          </span>
        </div>
      </div>

      <div v-if="order.comment" class="comment-block">
        <span class="comment-label">
          <PosUiIcon name="comment-outline" class="comment-label-icon" />
          {{ $t('posV2.orderComments') }}
        </span>
        <p class="comment-text">{{ order.comment }}</p>
      </div>
    </div>

    <!-- Custom fields + delivery notes (collapsible) -->
    <details v-if="hasExtras" class="extras-section">
      <summary class="extras-toggle">
        <PosUiIcon name="information-outline" class="toggle-icon" />
        <span>{{ $t('posV2.moreInfo') }}</span>
        <PosUiIcon name="chevron-down" class="toggle-chevron" />
      </summary>

      <div class="extras-content">
        <div
          v-for="orderField in filledCustomFields"
          :key="orderField.fieldId"
          class="extra-item"
        >
          <span class="extra-label">{{ orderField.field?.title }}</span>
          <span class="extra-value">{{ orderField.value }}</span>
        </div>

        <div v-if="order.userAddressComments" class="extra-item">
          <span class="extra-label">{{ $t('posV2.deliveryNotes') }}</span>
          <p class="extra-text">{{ order.userAddressComments }}</p>
        </div>
      </div>
    </details>

    <!-- Origin footer -->
    <div class="origin-footer">
      <span>
        {{ $t('posV2.fromSource') }} <strong>{{ originSourceText }}</strong>
      </span>
      <span v-if="order.user">
        {{ $t('posV2.bySource') }} <strong>{{ order.user.name }}</strong>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-view-info {
    @apply tw-flex tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-neutral-200 tw-bg-white;

    .info-header {
      @apply tw-border-b tw-border-neutral-100 tw-p-3;

      .header-row {
        @apply tw-mb-2 tw-flex tw-items-start tw-justify-between tw-gap-2;

        .title-group {
          @apply tw-flex tw-min-w-0 tw-items-center tw-gap-1.5;

          .title-icon {
            @apply tw-flex-shrink-0 tw-text-lg tw-text-orange-600;
          }

          .title {
            @apply tw-truncate tw-text-lg tw-font-bold tw-text-neutral-900;
          }
        }

        .order-id {
          @apply tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1 tw-font-mono tw-text-xs tw-text-neutral-400 hover:tw-text-neutral-600;

          .copy-icon {
            @apply tw-text-sm tw-opacity-50;
          }

          &:hover .copy-icon {
            @apply tw-opacity-100;
          }
        }
      }

      .chips-row {
        @apply tw-flex tw-flex-wrap tw-items-center tw-gap-1.5;
      }
    }

    .meta-section {
      @apply tw-space-y-2 tw-border-b tw-border-neutral-100 tw-px-3 tw-py-2;

      .meta-grid {
        @apply tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-2;

        .meta-item {
          @apply tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5;

          &--wide {
            @apply tw-col-span-2;
          }

          .meta-label {
            @apply tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-neutral-500;

            .meta-icon {
              @apply tw-text-xs;
            }
          }

          .meta-value {
            @apply tw-text-sm tw-font-medium tw-text-neutral-900;

            .club-member-number {
              @apply tw-ml-1 tw-text-xs tw-font-normal tw-text-neutral-500;
            }
          }

          .club-pills {
            @apply tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-1;

            .club-pill {
              @apply tw-tabular-nums;
            }
          }

          .meta-phone {
            @apply tw-flex tw-items-center tw-gap-2;

            .phone-link {
              @apply tw-text-sky-700 hover:tw-underline;
            }

            .phone-action {
              @apply tw-flex tw-h-7 tw-w-7 tw-items-center tw-justify-center tw-rounded-full tw-text-emerald-600 hover:tw-bg-emerald-50;
            }
          }

          .meta-address {
            @apply tw-flex tw-items-start tw-gap-1;

            .address-text {
              @apply tw-min-w-0 tw-flex-1;
            }

            .address-action {
              @apply tw-flex tw-h-7 tw-w-7 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-text-neutral-400 hover:tw-bg-neutral-100 hover:tw-text-neutral-700;
            }
          }
        }
      }

      .comment-block {
        @apply tw-flex tw-flex-col tw-gap-1;

        .comment-label {
          @apply tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-neutral-500;

          .comment-label-icon {
            @apply tw-text-sm;
          }
        }

        .comment-text {
          @apply tw-mb-0 tw-whitespace-pre-wrap tw-text-sm tw-leading-relaxed tw-text-neutral-700;
        }
      }
    }

    .extras-section {
      @apply tw-border-b tw-border-neutral-100;

      .extras-toggle {
        @apply tw-flex tw-cursor-pointer tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-neutral-500 tw-transition-colors hover:tw-bg-neutral-50;

        &::-webkit-details-marker {
          display: none;
        }

        .toggle-icon {
          @apply tw-text-sm;
        }

        .toggle-chevron {
          @apply tw-ml-auto tw-text-sm tw-transition-transform;
        }
      }

      &[open] .extras-toggle .toggle-chevron {
        @apply tw-rotate-180;
      }

      .extras-content {
        @apply tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-pb-3;

        .extra-item {
          @apply tw-flex tw-flex-col tw-gap-0.5;

          .extra-label {
            @apply tw-text-xs tw-text-neutral-500;
          }

          .extra-value {
            @apply tw-text-sm tw-font-medium tw-text-neutral-900;
          }

          .extra-text {
            @apply tw-mb-0 tw-whitespace-pre-wrap tw-text-sm tw-leading-relaxed tw-text-neutral-700;
          }
        }
      }
    }

    .origin-footer {
      @apply tw-space-x-1 tw-px-3 tw-py-2 tw-text-xs tw-text-neutral-500;
    }
  }
</style>
